import {
  AutoExcludedReason,
  EmailRecipientPreviewUser,
  EmailRecipientsPreview,
  PreviouslyEmailedRecipient,
} from "../types";

const AUTO_EXCLUDED_REASON_LABEL_MAP: Record<AutoExcludedReason, string> = {
  alreadyLoggedIn: "Already logged in",
  noPendingTeamInvite: "No pending team invite",
  noActiveTEAMscan: "No active TEAMscan",
  eppAlreadyCompleted: "EPP already completed",
};

// A reason the API adds later would otherwise render as an empty cell.
export const getAutoExcludedReasonLabel = (reason: AutoExcludedReason) =>
  AUTO_EXCLUDED_REASON_LABEL_MAP[reason] ?? "Not eligible";

export type RecipientCsvStatus =
  | "Included"
  | "Manually excluded"
  | "Automatically excluded";

export const RECIPIENTS_CSV_HEADERS = [
  { key: "name", label: "Name" },
  { key: "emailAddress", label: "Email" },
  { key: "status", label: "Status" },
  { key: "exclusionReason", label: "Exclusion Reason" },
  { key: "hasEmailBeenSentBefore", label: "Has Email Sent Before" },
  { key: "lastEmailedAt", label: "Last Emailed" },
];

export interface RecipientCsvRow {
  name: string;
  emailAddress: string;
  status: RecipientCsvStatus;
  exclusionReason: string;
  hasEmailBeenSentBefore: "Yes" | "No";
  lastEmailedAt: string;
  [key: string]: string;
}

interface Options {
  // Only the previously emailed recipients carry an address on the preview, so anyone else
  // has to be looked up against the company users already in the store.
  getEmailAddress?: (userAccountId: number) => string | undefined;
  formatDate?: (isoDate: string) => string;
}

const getFullName = (user: EmailRecipientPreviewUser) =>
  `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();

const getPreviouslyEmailedCells = (
  emailedBefore: PreviouslyEmailedRecipient | undefined,
  formatDate: (isoDate: string) => string
) => {
  if (!emailedBefore?.lastEmailSent) {
    return { hasEmailBeenSentBefore: "No" as const, lastEmailedAt: "" };
  }
  return {
    hasEmailBeenSentBefore: "Yes" as const,
    lastEmailedAt: formatDate(emailedBefore.lastEmailSent),
  };
};

/**
 * One row per person the preview resolved, whether or not they are still being sent to.
 *
 * Excluded people have to stay in the export - the reason an admin downloads this is to
 * check who is being left out, and a file containing only the included rows cannot answer
 * that. Manual exclusions are read off the recipients list rather than the id array
 * directly: the selection can change after someone is excluded, leaving stale ids that no
 * longer match anyone in this preview.
 */
export const getRecipientsCsvRows = (
  preview: EmailRecipientsPreview | null,
  excludedUserAccountIds: number[],
  { getEmailAddress, formatDate = (isoDate) => isoDate }: Options = {}
): RecipientCsvRow[] => {
  if (!preview) {
    return [];
  }

  const excludedIdSet = new Set(excludedUserAccountIds);
  const previouslyEmailed =
    preview.eligibleRecipientsThatReceivedEmailsBefore ?? {};

  const getEmailCell = (userAccountId: number) =>
    previouslyEmailed[userAccountId]?.emailAddress ??
    getEmailAddress?.(userAccountId) ??
    "";

  const recipientRows = (preview.recipients ?? []).map((user) => ({
    name: getFullName(user),
    emailAddress: getEmailCell(user.userAccountId),
    status: excludedIdSet.has(user.userAccountId)
      ? ("Manually excluded" as const)
      : ("Included" as const),
    exclusionReason: "",
    ...getPreviouslyEmailedCells(
      previouslyEmailed[user.userAccountId],
      formatDate
    ),
  }));

  const autoExcludedRows = (preview.autoExcluded ?? []).map((user) => ({
    name: getFullName(user),
    emailAddress: getEmailCell(user.userAccountId),
    status: "Automatically excluded" as const,
    exclusionReason: getAutoExcludedReasonLabel(user.reason),
    ...getPreviouslyEmailedCells(
      previouslyEmailed[user.userAccountId],
      formatDate
    ),
  }));

  // Grouped the same way the screen above it is, so the file reads as the list they saw.
  return [
    ...recipientRows.filter(({ status }) => status === "Included"),
    ...recipientRows.filter(({ status }) => status === "Manually excluded"),
    ...autoExcludedRows,
  ];
};
